import {
  beschreibungOhneAnrede, rollenbezeichnungen, ROLLENSYMBOL, sortiereRollen, type Personenangabe, type Rolle, type Rollenbezug,
} from "@dj/domain/gemeinsam";
import { Symbol } from "./Symbol";

/**
 * Person mit Bezeichnung (oberflaeche.md §2.1, §2.2; ADR-0025): Name und Bezeichnung als zwei getrennte Textelemente,
 * die Bezeichnung entsteht erst hier aus Anrede und neutralen Rollen. Mit Anrede die Rollenbezeichnung(en) in der Form
 * der Anrede („Schützin, Standschnallerin“), ohne Anrede je Rolle ihr Symbol und ihre Beschreibung („Stand 7a“,
 * „leitet die Jagd“) — nie eine männliche oder typografische Doppelform. Gestaltet als Titel und Unterzeile einer
 * Listenzeile (design/README §9.8, `zeile__t`, `zeile__u`); der Eintrag darum (`article`) gehört der Ansicht.
 */
export function PersonMitBezeichnung({ person, rollen, bezug }: { person: Personenangabe; rollen: readonly Rolle[]; bezug?: Rollenbezug }) {
  const sortiert = sortiereRollen(rollen);
  const bezeichnung = sortiert.length ? rollenbezeichnungen(sortiert, person.Anrede) : null;
  return (
    <>
      <span className="zeile__t person__name">{person.Name}</span>{" "}
      {bezeichnung !== null ? (
        <span className="zeile__u person__bezeichnung"><Symbol name={ROLLENSYMBOL[sortiert[0]!]} /><span>{bezeichnung}</span></span>
      ) : (
        <BeschreibungOhneAnrede rollen={sortiert} bezug={bezug} />
      )}
    </>
  );
}

/** Bezeichnung einer Rolle bzw. mehrerer Rollen allein (eigene Rolle, §2.1): mit Anrede die Form der Anrede, sonst Beschreibung. */
export function EigeneRolle({ anrede, rollen, bezug }: { anrede: Personenangabe["Anrede"]; rollen: readonly Rolle[]; bezug?: Rollenbezug }) {
  const sortiert = sortiereRollen(rollen);
  const bezeichnung = sortiert.length ? rollenbezeichnungen(sortiert, anrede) : null;
  if (bezeichnung !== null) return <span className="person__bezeichnung"><Symbol name={ROLLENSYMBOL[sortiert[0]!]} /><span>{bezeichnung}</span></span>;
  return <BeschreibungOhneAnrede rollen={sortiert} bezug={bezug} />;
}

function BeschreibungOhneAnrede({ rollen, bezug }: { rollen: readonly Rolle[]; bezug?: Rollenbezug }) {
  const gesehen = new Set<string>();
  const teile = rollen.map((r) => ({ rolle: r, text: beschreibungOhneAnrede(r, bezug) })).filter((t) => !gesehen.has(t.text) && gesehen.add(t.text));
  return (
    <>
      {teile.map((t) => (
        <span key={t.rolle} className="zeile__u person__bezeichnung"><Symbol name={ROLLENSYMBOL[t.rolle]} /><span>{t.text}</span></span>
      ))}
    </>
  );
}
